import { useDiet } from "@/contexts/DietContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Send, Users, MessageCircle, Sparkles, Heart, Loader2 } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const benefits = [
  {
    icon: MessageCircle,
    title: "Suporte direto",
    description: "Tire dúvidas sobre dieta e uso da plataforma com a nossa equipe.",
  },
  {
    icon: Users,
    title: "Troca de experiências",
    description: "Converse com outros alunos, compartilhe resultados e receba motivação.",
  },
  {
    icon: Sparkles,
    title: "Novidades em primeira mão",
    description: "Receitas exclusivas, dicas e atualizações da plataforma antes de todo mundo.",
  },
  {
    icon: Heart,
    title: "Ambiente acolhedor",
    description: "Comunidade respeitosa, focada em saúde, bem-estar e evolução de cada um.",
  },
];

const Comunidade = () => {
  const { selectedPlan } = useDiet();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const tiers = ["basic", "plus", "complete"];
  const hasAccess = tiers.indexOf(selectedPlan || "") >= tiers.indexOf("complete");

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
          <Lock className="w-7 h-7 text-muted-foreground" />
        </div>
        <h1 className="text-2xl font-bold text-foreground">Comunidade Exclusiva</h1>
        <p className="text-muted-foreground max-w-sm">
          O grupo da nossa comunidade no Telegram está disponível apenas no plano{" "}
          <strong>PRO FIT MAX</strong>. Faça upgrade para entrar.
        </p>
        <Button onClick={() => navigate("/plans")}>Ver Planos</Button>
      </div>
    );
  }

  const handleJoin = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-community-link");
      if (error) throw error;
      const url = (data as { url?: string })?.url;
      if (!url) throw new Error("Link indisponível");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro ao obter link";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Comunidade</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Grupo exclusivo no Telegram para alunos do plano PRO FIT MAX
        </p>
      </div>

      <Card className="overflow-hidden">
        <div className="bg-primary/10 px-6 py-8 flex flex-col items-center text-center gap-3">
          <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
            <Send className="w-8 h-8 text-primary-foreground" />
          </div>
          <h2 className="text-xl font-bold text-foreground">
            Bem-vindo(a) à nossa comunidade!
          </h2>
          <p className="text-sm text-muted-foreground max-w-md">
            Entre no nosso grupo do Telegram e faça parte de uma comunidade ativa de
            pessoas em busca de uma vida mais saudável.
          </p>
        </div>

        <CardContent className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map((b) => (
              <div key={b.title} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <b.icon className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground text-sm leading-tight">
                    {b.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">{b.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-muted/50 p-4 text-xs text-muted-foreground space-y-1">
            <p className="font-semibold text-foreground">Regras de convivência:</p>
            <p>• Respeite todos os membros do grupo.</p>
            <p>• Não compartilhe informações pessoais ou conteúdos externos sem autorização.</p>
            <p>• Mantenha o foco em saúde, alimentação e bem-estar.</p>
          </div>

          <Button size="lg" className="w-full" onClick={handleJoin} disabled={loading}>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Entrar no grupo do Telegram
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Comunidade;
